/**
 * usePhotoStore — IndexedDB CRUD wrapper for photo evidence Blobs.
 *
 * FT-341 (2026-07-08) — foundation for FT-340 photo evidence project.
 * See: Maritime-Agents/seaready-app/FT-340-photos/DESIGN-BRIEF.md §2.
 *
 * Responsibilities:
 *  - Persist photo + thumb Blobs to Dexie tables `photos` / `photoThumbs`.
 *  - Vessel-isolate every query via the compound `[vesselId+recordId]` index.
 *  - Manage Blob URL lifecycle: cache per photoId, revoke on unmount + on
 *    vesselId prop change (vessel switch).
 *  - Cascade delete photos by record.
 *  - Expose vessel photo count for quota telemetry.
 *
 * Returns nothing but references on the record (PhotoRef); the Blob itself
 * stays in IDB.
 */

import { useCallback, useEffect, useMemo, useRef } from 'react';
import { db } from '../../../../lib/db';
import { pushOutbox, CHANGE_TYPES } from '../../../../lib/outbox';
import { buildPhotoCloudRecord } from './buildPhotoCloudRecord';
import { devWarn } from '../../../../utils/devLog';
import {
  computePhotoPushBodyBytes,
  PHOTO_PUSH_BODY_BUDGET_BYTES,
  PhotoTooLargeError,
} from '../../../../utils/photoPayloadBudget';
import { recordPhotoEnqueueFailure, clearPhotoEnqueueFailure } from '../../../../lib/photoEnqueueFailures';

/**
 * Shape of the reference stored on the parent record.
 * @typedef {{ photoId: string, capturedAt: number, capturedBy: string, caption?: string }} PhotoRef
 */

/**
 * @param {string} vesselId  Required. All queries are scoped to this vessel.
 * @returns Photo-store API — see JSDoc on individual methods below.
 */
export function usePhotoStore(vesselId) {
  // Map<photoId, objectURL> — one canonical URL per photo, revoked on cleanup.
  const urlCacheRef = useRef(new Map());
  const currentVesselRef = useRef(vesselId);

  const revokeAll = useCallback(() => {
    const cache = urlCacheRef.current;
    cache.forEach((url) => {
      try {
        URL.revokeObjectURL(url);
      } catch {
        /* ignore — some URLs may have been revoked already */
      }
    });
    cache.clear();
  }, []);

  /**
   * FT-436 fix: revoke + evict BOTH cached object-URLs for a single photoId
   * (the thumb key `photoId` AND the full-blob key `full:photoId`).
   *
   * Why this is load-bearing: profile photos use a DETERMINISTIC photoId
   * (`profile:<vesselId>`), so RE-UPLOADING a vessel's photo in the same session
   * reuses the same id. Without eviction, getThumb / getFullUrl would return the
   * OLD cached object-URL (pointing at the previous blob) and the card would show
   * the STALE image until unmount / vessel-switch. Evicting here forces the next
   * getThumb / getFullUrl to mint a fresh URL from the just-written blob, so a
   * re-uploaded photo repaints immediately.
   */
  const invalidateUrlCache = useCallback((photoId) => {
    if (!photoId) return;
    const cache = urlCacheRef.current;
    [photoId, `full:${photoId}`].forEach((key) => {
      const url = cache.get(key);
      if (url) {
        try { URL.revokeObjectURL(url); } catch { /* already revoked */ }
        cache.delete(key);
      }
    });
  }, []);

  // Vessel switch: prop changed → revoke cached URLs owned by prev vessel.
  useEffect(() => {
    if (currentVesselRef.current !== vesselId) {
      revokeAll();
      currentVesselRef.current = vesselId;
    }
  }, [vesselId, revokeAll]);

  // Unmount cleanup — belt-and-braces for navigation away without vessel switch.
  useEffect(() => () => revokeAll(), [revokeAll]);

  /**
   * Save a photo (full blob + thumb) to IDB.
   * @param {Object} photoData  Row for `photos` table minus vesselId (added here).
   *   Required: photoId, recordType, recordId, blob, mime, capturedAt, capturedBy.
   *   Optional: width, height, bytes, caption.
   * @param {Blob} thumbBlob    JPEG thumbnail Blob (256×256 target — see brief §2.5).
   * @returns {Promise<PhotoRef>} Reference for the parent record.
   */
  const savePhoto = useCallback(async (photoData, thumbBlob) => {
    if (!vesselId) throw new Error('[usePhotoStore] vesselId is required');
    if (!photoData?.photoId) throw new Error('[usePhotoStore] photoData.photoId is required');
    if (!(photoData.blob instanceof Blob)) throw new Error('[usePhotoStore] photoData.blob must be a Blob');
    if (!(thumbBlob instanceof Blob)) throw new Error('[usePhotoStore] thumbBlob must be a Blob');

    // FT-577 HARD BACKSTOP: every photo writer (capture pipeline, vessel
    // profile, any future caller) funnels through here. Refuse — BEFORE any
    // write — a photo whose real /sync/push body would exceed the budget, so a
    // photo can never be "saved" locally and then silently fail to upload.
    // Callers already fit photos to the budget; this only fires if one didn't.
    const bodyBytes = computePhotoPushBodyBytes({
      photoData,
      fullBytes: photoData.blob.size,
      fullMime: photoData.blob.type,
      thumbBytes: thumbBlob.size,
      thumbMime: thumbBlob.type,
      vesselId,
    });
    if (bodyBytes > PHOTO_PUSH_BODY_BUDGET_BYTES) {
      throw new PhotoTooLargeError({ bodyBytes, budgetBytes: PHOTO_PUSH_BODY_BUDGET_BYTES });
    }

    const photoRow = { ...photoData, vesselId };
    const thumbRow = {
      photoId: photoData.photoId,
      vesselId,
      blob: thumbBlob,
      mime: thumbBlob.type || 'image/jpeg',
      bytes: thumbBlob.size,
    };

    // Two-table write. On QuotaExceededError (or any IDB failure), let it
    // propagate so the capture pipeline can surface a toast.
    try {
      await db.transaction('rw', db.photos, db.photoThumbs, async () => {
        await db.photos.put(photoRow);
        await db.photoThumbs.put(thumbRow);
      });
    } catch (err) {
      // Preserve QuotaExceededError shape for capture-pipeline handling.
      throw err;
    }

    // FT-436 fix: the blobs for this photoId just changed on disk. Evict any
    // cached object-URLs (thumb + full) so a re-upload at the SAME id (e.g. a
    // deterministic `profile:<vesselId>` photo) repaints from the new blob
    // instead of the stale cached URL. Must run AFTER the write commits and
    // BEFORE the return, so the next getThumb / getFullUrl mints a fresh URL.
    invalidateUrlCache(photoData.photoId);

    // FT-344 (2026-07-08): enqueue for cloud sync via outbox.
    // Photos DO NOT flow through the `sr:storage:set` path — the photos/photoThumbs
    // tables live outside `db.storage`, so writes don't fire that event. Direct-
    // to-outbox is the ONLY push path per DESIGN-BRIEF §3.4.
    // A base64 conversion failure or outbox enqueue failure must NOT roll back
    // the IDB write (photo is safe locally). FT-577: this is no longer silent —
    // it is recorded (surfaced by the header sync badge) and logged in prod;
    // the boot-time reconcile (photoPullMerge) re-queues it on next start.
    try {
      const cloudRecord = await buildPhotoCloudRecord({
        photoData: photoRow,
        thumbBlob,
        vesselId,
      });
      // pushOutbox swallows its own errors and returns null on failure.
      const entryId = await pushOutbox({
        collection: 'sr.photos.v1',
        type: CHANGE_TYPES.CREATE,
        recordId: photoData.photoId,
        data: cloudRecord,
        vesselId,
      });
      if (!entryId) throw new Error('outbox write failed');
      clearPhotoEnqueueFailure(photoData.photoId);
    } catch (err) {
      recordPhotoEnqueueFailure(photoData.photoId, err?.message);
      // eslint-disable-next-line no-console -- FT-577: must be visible in prod (devWarn is a no-op there)
      console.warn('[FT-577] photo saved on device but NOT queued for upload', {
        photoId: photoData.photoId,
        error: err?.message,
      });
    }

    return {
      photoId: photoData.photoId,
      capturedAt: photoData.capturedAt,
      capturedBy: photoData.capturedBy,
      ...(photoData.caption ? { caption: photoData.caption } : {}),
    };
  }, [vesselId, invalidateUrlCache]);

  /**
   * Fetch the full photo blob + metadata. Vessel-isolated.
   */
  const getPhoto = useCallback(async (photoId) => {
    if (!vesselId || !photoId) return null;
    const row = await db.photos.get(photoId);
    if (!row || row.vesselId !== vesselId) return null;
    const { blob, ...meta } = row;
    return { blob, meta };
  }, [vesselId]);

  /**
   * Return an object-URL for the thumbnail — cached per photoId for the
   * lifetime of this hook instance / vessel context.
   */
  const getThumb = useCallback(async (photoId) => {
    if (!vesselId || !photoId) return null;
    const cached = urlCacheRef.current.get(photoId);
    if (cached) {
      // Also need the mime for consumers; look up cheaply (indexed).
      const row = await db.photoThumbs.get(photoId);
      if (!row || row.vesselId !== vesselId) return null;
      return { url: cached, mime: row.mime };
    }
    const row = await db.photoThumbs.get(photoId);
    if (!row || row.vesselId !== vesselId) return null;
    const url = URL.createObjectURL(row.blob);
    urlCacheRef.current.set(photoId, url);
    return { url, mime: row.mime };
  }, [vesselId]);

  /**
   * FT-436: return an object-URL for the FULL photo blob (not the thumbnail) —
   * cached per photoId exactly like getThumb, so the fleet card can render the
   * full-resolution compressed image instead of upscaling a small thumb.
   * Cache key is prefixed `full:` so it never collides with the thumb URL for
   * the same photoId (both live in the same urlCache and are revoked together).
   */
  const getFullUrl = useCallback(async (photoId) => {
    if (!vesselId || !photoId) return null;
    const cacheKey = `full:${photoId}`;
    const cached = urlCacheRef.current.get(cacheKey);
    if (cached) {
      const row = await db.photos.get(photoId);
      if (!row || row.vesselId !== vesselId) return null;
      return { url: cached, mime: row.mime };
    }
    const row = await db.photos.get(photoId);
    if (!row || row.vesselId !== vesselId || !row.blob) return null;
    const url = URL.createObjectURL(row.blob);
    urlCacheRef.current.set(cacheKey, url);
    return { url, mime: row.mime };
  }, [vesselId]);

  /**
   * List PhotoRefs for a record (vessel-scoped via compound index).
   * @returns {Promise<PhotoRef[]>}
   */
  const getPhotosByRecord = useCallback(async (recordType, recordId) => {
    if (!vesselId || !recordId) return [];
    const rows = await db.photos
      .where('[vesselId+recordId]')
      .equals([vesselId, recordId])
      .toArray();
    return rows
      .filter((r) => r.recordType === recordType)
      .sort((a, b) => a.capturedAt - b.capturedAt)
      .map((r) => ({
        photoId: r.photoId,
        capturedAt: r.capturedAt,
        capturedBy: r.capturedBy,
        ...(r.caption ? { caption: r.caption } : {}),
      }));
  }, [vesselId]);

  /**
   * FT-343 pre-merge fix (2026-07-08): enqueue a DELETE tombstone to the outbox.
   * Tombstone shape carries `deleted: true` + fresh `updatedAt` so the cloud
   * merge treats it as newer than any existing CREATE for this photoId; other
   * devices see it on the next pull and photoPullMerge cascades the delete.
   * Fail-silent (same policy as savePhoto): the local IDB delete has already
   * committed and MUST NOT be rolled back if the outbox push fails.
   */
  const enqueuePhotoDelete = useCallback(async (photoId) => {
    try {
      await pushOutbox({
        collection: 'sr.photos.v1',
        type: CHANGE_TYPES.DELETE,
        recordId: photoId,
        data: {
          id: photoId,
          deleted: true,
          vesselId,
          updatedAt: Date.now(),
        },
        vesselId,
      });
    } catch (err) {
      devWarn('[FT-343] photo DELETE outbox enqueue failed (photo removed locally)', {
        photoId,
        error: err?.message,
      });
    }
  }, [vesselId]);

  /**
   * Delete one photo (both tables). Also revokes + evicts the cached URL.
   */
  const deletePhoto = useCallback(async (photoId) => {
    if (!vesselId || !photoId) return;
    const row = await db.photos.get(photoId);
    if (!row || row.vesselId !== vesselId) return; // vessel-scoped guard
    await db.transaction('rw', db.photos, db.photoThumbs, async () => {
      await db.photos.delete(photoId);
      await db.photoThumbs.delete(photoId);
    });
    // FT-436 fix: evict BOTH cached object-URLs (thumb `photoId` + full
    // `full:photoId`). The prior code revoked only the bare thumb key, leaking
    // the full-blob object-URL; invalidateUrlCache closes both.
    invalidateUrlCache(photoId);
    // FT-343 pre-merge fix (2026-07-08): propagate delete across devices.
    await enqueuePhotoDelete(photoId);
  }, [vesselId, enqueuePhotoDelete, invalidateUrlCache]);

  /**
   * Cascade delete every photo attached to a record.
   */
  const deleteByRecord = useCallback(async (recordType, recordId) => {
    if (!vesselId || !recordId) return;
    const rows = await db.photos
      .where('[vesselId+recordId]')
      .equals([vesselId, recordId])
      .toArray();
    const targetIds = rows.filter((r) => r.recordType === recordType).map((r) => r.photoId);
    if (targetIds.length === 0) return;
    await db.transaction('rw', db.photos, db.photoThumbs, async () => {
      await db.photos.bulkDelete(targetIds);
      await db.photoThumbs.bulkDelete(targetIds);
    });
    targetIds.forEach((pid) => {
      const cached = urlCacheRef.current.get(pid);
      if (cached) {
        try { URL.revokeObjectURL(cached); } catch { /* noop */ }
        urlCacheRef.current.delete(pid);
      }
    });
    // FT-343 pre-merge fix (2026-07-08): enqueue per-photo tombstone. One
    // outbox entry per photoId keeps the cross-device merge simple; if
    // bandwidth becomes a concern later we can batch.
    for (const pid of targetIds) {
      // eslint-disable-next-line no-await-in-loop
      await enqueuePhotoDelete(pid);
    }
  }, [vesselId, enqueuePhotoDelete]);

  /**
   * Vessel-scoped photo count. Powers quota-monitor telemetry (FT-340 §7.3).
   */
  const getVesselPhotoCount = useCallback(async () => {
    if (!vesselId) return 0;
    return db.photos.where('vesselId').equals(vesselId).count();
  }, [vesselId]);

  return useMemo(() => ({
    savePhoto,
    getPhoto,
    getThumb,
    getFullUrl,
    getPhotosByRecord,
    deletePhoto,
    deleteByRecord,
    getVesselPhotoCount,
  }), [savePhoto, getPhoto, getThumb, getFullUrl, getPhotosByRecord, deletePhoto, deleteByRecord, getVesselPhotoCount]);
}

export default usePhotoStore;
